import { z } from 'zod';

import { CounterpartyRefKindSchema, CounterpartySnapshotSchema } from '../counterparties/index.js';
import { CoverShipmentStatusSchema } from '../cover-shipments/index.js';
import { shipmentFollowUpComparisonStatusValues } from '../shipments/list-scale-tickets.dto.js';
import { IsoDateTimeSchema, ShipmentStatusSchema } from '../shipments/shipments-types.js';
import type {
  CoverOutboundRow,
  ListOutboundRequest,
  ListOutboundResponse,
  OutboundDateSummary,
  OutboundDownstreamCoverSummary,
  OutboundDownstreamInboundReceiptSummary,
  OutboundListSummary,
  OutboundRelationshipSourceSummary,
  OutboundRelationshipSummary,
  OutboundScaleTicketSummary,
  OutboundStatusSummary,
  OutboundWeightSummary,
  RegularOutboundRow,
} from './outbound-list.dto.js';
import { outboundCurrentStepValues, outboundRowTypeValues } from './outbound-list.dto.js';

const nonNegativeNumberSchema = z.number().min(0);
const nonNegativeIntSchema = z.number().int().min(0);
const trimmedStringSchema = z.string().trim().min(1);

const validateDateRange = (value: unknown, ctx: z.RefinementCtx): void => {
  if (!value || typeof value !== 'object') {
    return;
  }
  const createdFrom = (value as { createdFrom?: string }).createdFrom;
  const createdTo = (value as { createdTo?: string }).createdTo;
  if (typeof createdFrom !== 'string' || typeof createdTo !== 'string') {
    return;
  }

  const fromMs = Date.parse(createdFrom);
  const toMs = Date.parse(createdTo);
  if (!Number.isNaN(fromMs) && !Number.isNaN(toMs) && fromMs >= toMs) {
    ctx.addIssue({
      code: 'custom',
      message: 'createdFrom must be before createdTo.',
      path: ['createdTo'],
    });
  }
};

export const OutboundCurrentStepSchema = z.enum(outboundCurrentStepValues);
export const OutboundRowTypeSchema = z.enum(outboundRowTypeValues);

export const ListOutboundRequestSchema: z.ZodType<ListOutboundRequest> = z
  .object({
    companyId: z.uuid(),
    types: z.array(OutboundRowTypeSchema).min(1).optional(),
    materialId: z.uuid().optional(),
    currentSteps: z.array(OutboundCurrentStepSchema).min(1).optional(),
    createdFrom: IsoDateTimeSchema.optional(),
    createdTo: IsoDateTimeSchema.optional(),
    needsScaleTicket: z.boolean().optional(),
    needsFollowUp: z.boolean().optional(),
  })
  .strict()
  .superRefine(validateDateRange);

export const OutboundDateSummarySchema: z.ZodType<OutboundDateSummary> = z
  .object({
    reportPeriodStartAt: IsoDateTimeSchema,
    reportPeriodEndAt: IsoDateTimeSchema,
    createdAt: IsoDateTimeSchema,
    departedAt: IsoDateTimeSchema.nullable(),
    voidedAt: IsoDateTimeSchema.nullable(),
    sortAt: IsoDateTimeSchema,
  })
  .strict();

export const OutboundWeightSummarySchema: z.ZodType<OutboundWeightSummary> = z
  .object({
    documentTotalWeightLb: nonNegativeNumberSchema.nullable(),
    huSelectedPhysicalWeightLb: nonNegativeNumberSchema.nullable(),
    processorReceivedWeightLb: nonNegativeNumberSchema.nullable(),
  })
  .strict();

export const OutboundStatusSummarySchema: z.ZodType<OutboundStatusSummary> = z
  .object({
    sourceStatus: z.union([ShipmentStatusSchema, CoverShipmentStatusSchema]),
    currentStep: OutboundCurrentStepSchema,
  })
  .strict();

export const OutboundScaleTicketSummarySchema: z.ZodType<OutboundScaleTicketSummary> = z
  .object({
    internalTicketCount: nonNegativeIntSchema,
    processorTicketCount: nonNegativeIntSchema,
    latestProcessorWeighedAt: IsoDateTimeSchema.nullable(),
    hasCompleteProcessorTicket: z.boolean(),
    followUpComparisonStatus: z.enum(shipmentFollowUpComparisonStatusValues).nullable(),
    needsScaleTicket: z.boolean(),
    needsFollowUp: z.boolean(),
  })
  .strict();

export const OutboundRelationshipSourceSummarySchema: z.ZodType<OutboundRelationshipSourceSummary> =
  z
    .object({
      receiptId: z.uuid(),
      reportNumber: trimmedStringSchema.nullable(),
      receivedWeightLb: nonNegativeNumberSchema.nullable(),
      receivedAt: IsoDateTimeSchema.nullable(),
      sourceDisplayName: trimmedStringSchema.nullable(),
      sourceKind: CounterpartyRefKindSchema.nullable(),
    })
    .strict();

export const OutboundDownstreamInboundReceiptSummarySchema: z.ZodType<OutboundDownstreamInboundReceiptSummary> =
  z
    .object({
      receiptId: z.uuid(),
      reportNumber: trimmedStringSchema.nullable(),
      receivedWeightLb: nonNegativeNumberSchema.nullable(),
      confirmedAt: IsoDateTimeSchema.nullable(),
    })
    .strict();

export const OutboundDownstreamCoverSummarySchema: z.ZodType<OutboundDownstreamCoverSummary> =
  z
    .object({
      coverShipmentId: z.uuid(),
      reportNumber: trimmedStringSchema.nullable(),
      status: CoverShipmentStatusSchema,
    })
    .strict();

export const OutboundRelationshipSummarySchema: z.ZodType<OutboundRelationshipSummary> = z
  .object({
    coverAttachmentCount: nonNegativeIntSchema,
    inboundSourceReportNumbers: z.array(trimmedStringSchema),
    sourceSummaries: z.array(OutboundRelationshipSourceSummarySchema),
    downstreamInboundReceipt: OutboundDownstreamInboundReceiptSummarySchema.nullable(),
    downstreamCover: OutboundDownstreamCoverSummarySchema.nullable(),
  })
  .strict();

const OutboundRowBaseSchema = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    stationId: z.uuid(),
    materialId: z.uuid(),
    materialDisplayName: trimmedStringSchema.nullable(),
    receiver: CounterpartySnapshotSchema,
    receiverDisplayName: trimmedStringSchema,
    reportNumber: trimmedStringSchema.nullable(),
    dates: OutboundDateSummarySchema,
    status: OutboundStatusSummarySchema,
    weights: OutboundWeightSummarySchema,
    scaleTicket: OutboundScaleTicketSummarySchema,
    relationship: OutboundRelationshipSummarySchema,
  })
  .strict();

export const RegularOutboundRowSchema = OutboundRowBaseSchema.extend({
    type: z.literal('regular'),
    shipmentId: z.uuid(),
  }).strict() satisfies z.ZodType<RegularOutboundRow>;

export const CoverOutboundRowSchema = OutboundRowBaseSchema.extend({
    type: z.literal('cover'),
    coverShipmentId: z.uuid(),
  }).strict() satisfies z.ZodType<CoverOutboundRow>;

export const OutboundListRowSchema = z.discriminatedUnion('type', [
  RegularOutboundRowSchema,
  CoverOutboundRowSchema,
]) satisfies z.ZodType<RegularOutboundRow | CoverOutboundRow>;

export const OutboundListSummarySchema: z.ZodType<OutboundListSummary> = z
  .object({
    totalCount: nonNegativeIntSchema,
    regularCount: nonNegativeIntSchema,
    coverCount: nonNegativeIntSchema,
    needsScaleTicketCount: nonNegativeIntSchema,
    needsFollowUpCount: nonNegativeIntSchema,
  })
  .strict();

export const ListOutboundResponseSchema = z
  .object({
    items: z.array(OutboundListRowSchema),
    summary: OutboundListSummarySchema,
  })
  .strict() satisfies z.ZodType<ListOutboundResponse>;
